export type SubdomainInfo =
  | { type: "main" }
  | { type: "subdomain"; identifier: string }
  | { type: "custom_domain"; identifier: string };

export interface HostEnv {
  VITE_BASE_DOMAIN?: string;
  VITE_MARKETING_HOSTS?: string;
  VITE_RESERVED_SUBDOMAINS?: string;
}

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1"]);
const DEFAULT_RESERVED_SUBDOMAINS = "www,api,cdn,e,a,clerk,app";

function splitList(value: string | undefined): string[] {
  if (!value) return [];
  return value.split(",").map((s) => s.trim()).filter(Boolean);
}

function getDefaultEnv(): HostEnv {
  // Vite replaces import.meta.env at build time. Cast keeps Node/tsx happy when
  // the field is absent during unit testing — tests pass their own env.
  const env = (import.meta as unknown as { env?: Record<string, string> }).env;
  return {
    VITE_BASE_DOMAIN: env?.VITE_BASE_DOMAIN,
    VITE_MARKETING_HOSTS: env?.VITE_MARKETING_HOSTS,
    VITE_RESERVED_SUBDOMAINS: env?.VITE_RESERVED_SUBDOMAINS,
  };
}

export function createHostClassifier(env: HostEnv = getDefaultEnv()) {
  const marketingHosts = splitList(env.VITE_MARKETING_HOSTS ?? env.VITE_BASE_DOMAIN);
  const reservedSubdomains = splitList(env.VITE_RESERVED_SUBDOMAINS ?? DEFAULT_RESERVED_SUBDOMAINS);
  const baseDomain = env.VITE_BASE_DOMAIN;

  function isMarketingHost(host: string): boolean {
    if (LOCAL_HOSTS.has(host)) return true;
    return marketingHosts.includes(host);
  }

  function classifyHost(host: string): SubdomainInfo {
    if (LOCAL_HOSTS.has(host)) return { type: "main" };
    if (marketingHosts.includes(host)) return { type: "main" };

    if (baseDomain && host.endsWith(`.${baseDomain}`)) {
      const slug = host.slice(0, host.length - baseDomain.length - 1);
      if (reservedSubdomains.includes(slug)) return { type: "main" };
      if (slug) return { type: "subdomain", identifier: slug };
    }

    return { type: "custom_domain", identifier: host };
  }

  return { isMarketingHost, classifyHost };
}

const defaultClassifier = createHostClassifier();

export const isMarketingHost = defaultClassifier.isMarketingHost;
export const classifyHost = defaultClassifier.classifyHost;
